import { formatKopecks, priceKopecks } from '@krp/sale-ui'

import type { OwnPriceKind, OwnPriceRecord } from '../api/contracts'

/**
 * Цены своей базы словами экрана. Деньги показывает одна утилита (`formatKopecks`, CLAUDE.md §7); в модели они — копейки
 * целым числом. Цена действует с начала суток `validFrom` (сутки магазина) и до следующей записи того же вида.
 */
export type PriceInput = { ok: true; kopecks: number } | { ok: false; text: string }

const FORM = /^\d{1,13}(?:[.,]\d{1,2})?$/

/** Цена, как её набрал человек: рубли с запятой или точкой, до двух знаков после запятой; ноль допустим (служба принимает). */
export function parsePriceInput(text: string): PriceInput {
  const value = text.trim().replace(/\s/g, '')
  if (value === '') return { ok: false, text: 'Укажите цену' }
  if (!FORM.test(value)) return { ok: false, text: 'Цена — число в рублях, до двух знаков после запятой' }
  return { ok: true, kopecks: priceKopecks(value) }
}

const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/

/** Дата `yyyy-MM-dd` → «20.10.2026»; иное — как есть. */
export function dayText(day: string): string {
  if (!ISO_DAY.test(day)) return day
  const [year, month, date] = day.split('-')
  return `${date}.${month}.${year}`
}

/** Момент записи → «20.10.2026, 14:05» по часам этого компьютера. */
export function momentText(iso: string): string {
  const at = new Date(iso)
  if (Number.isNaN(at.getTime())) return iso
  return at.toLocaleString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

/** Сумма с валютой вида: рубли — общей утилитой денег, иная валюта — числом и кодом (её знака утилита не знает). */
export function moneyText(kopecks: number, currency: string): string {
  if (currency === 'RUB') return formatKopecks(kopecks)
  return `${formatKopecks(kopecks).replace(/\s*₽$/, '')} ${currency}`
}

export interface PriceHistoryRow {
  key: string
  priceTypeId: string
  priceTypeName: string
  validFrom: string
  validFromText: string
  priceText: string
  setBy: string
  setAtText: string
  /** Действует ли эта запись на выбранную дату (последняя запись вида не позже даты). */
  current: boolean
}

/**
 * История цен товара: новые сверху, у каждой — вид, с какого дня, кто и когда записал. Действующая на дату `on` — отмечена
 * словом и значком, а не только цветом.
 */
export function priceHistoryRows(records: readonly OwnPriceRecord[], kinds: readonly OwnPriceKind[], on: string): readonly PriceHistoryRow[] {
  const byId = new Map(kinds.map((kind) => [kind.id, kind]))
  const currentByKind = new Map<string, string>()
  for (const item of records) {
    if (item.validFrom > on) continue
    const known = currentByKind.get(item.priceTypeId)
    if (known === undefined || item.validFrom > known) currentByKind.set(item.priceTypeId, item.validFrom)
  }
  return [...records]
    .sort((a, b) => (a.validFrom === b.validFrom ? a.priceTypeId.localeCompare(b.priceTypeId) : b.validFrom.localeCompare(a.validFrom)))
    .map((item) => {
      const kind = byId.get(item.priceTypeId)
      return {
        key: `${item.priceTypeId}|${item.validFrom}`,
        priceTypeId: item.priceTypeId,
        priceTypeName: kind?.name ?? 'Вид цены не найден',
        validFrom: item.validFrom,
        validFromText: dayText(item.validFrom),
        priceText: moneyText(item.kopecks, kind?.currency ?? 'RUB'),
        setBy: item.setBy,
        setAtText: momentText(item.setAt),
        current: currentByKind.get(item.priceTypeId) === item.validFrom,
      }
    })
}

/** Сегодня по часам этого компьютера, `yyyy-MM-dd` (поле даты; «сегодня» службы — сутки магазина, она решает сама). */
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${now.getFullYear()}-${month}-${day}`
}

export function isIsoDay(value: string): boolean {
  if (!ISO_DAY.test(value)) return false
  const at = new Date(`${value}T00:00:00`)
  return !Number.isNaN(at.getTime()) && todayIso(at) === value
}
